import { ReactNode, useMemo, useState } from "react";
import { Box, ButtonBase, Collapse, Typography } from "@mui/material";
import {
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { grey } from "@mui/material/colors";
import { useToolRunSummary } from "../../ChatStreaming/services/api";

interface ToolRunGroupProps {
  /** Number of tool calls rolled up into this group */
  callCount: number;
  /** Names + status messages of the grouped tool calls, for the LLM label */
  tools?: { name: string; statusMessage?: string }[];
  /** Fetch the descriptive label (off while the run is still streaming) */
  summaryEnabled?: boolean;
  children: ReactNode;
}

/**
 * ToolRunGroup rolls a run of consecutive completed tool calls up into a
 * single line, expandable to the individual compact rows. The label is a
 * FastLLM-generated phrase describing the run ("Searched pump vibration
 * data and related alarms"), falling back to "Used N tools" while it
 * streams/loads or if generation fails. Children stay mounted while
 * collapsed so their side effects (artifact store writes in ToolResponse)
 * keep running.
 */
const ToolRunGroup = ({
  callCount,
  tools = [],
  summaryEnabled = false,
  children,
}: ToolRunGroupProps) => {
  const [expanded, setExpanded] = useState(false);

  const summaryTools = useMemo(
    () =>
      tools.map((tool) => ({
        name: tool.name,
        status_message: tool.statusMessage,
      })),
    [tools],
  );
  const { data: summary } = useToolRunSummary({
    tools: summaryTools,
    enabled: summaryEnabled,
  });

  const countLabel = `Used ${callCount} tool${callCount === 1 ? "" : "s"}`;
  const summaryLabel = summary?.trim();

  return (
    <Box data-testid="aa-tool-run-group" sx={{ mb: 0.5 }}>
      <ButtonBase
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          textAlign: "left",
          width: "100%",
          gap: 0.5,
          px: 0.5,
          py: 0.25,
          borderRadius: "4px",
          userSelect: "text",
          "&:hover": { bgcolor: grey[100] },
          transition: "background-color 0.15s ease",
        }}
      >
        {expanded ? (
          <ExpandMoreIcon sx={{ fontSize: 14, color: grey[500] }} />
        ) : (
          <ChevronRightIcon sx={{ fontSize: 14, color: grey[500] }} />
        )}
        <Typography
          variant="caption"
          noWrap
          sx={{ color: grey[600], fontWeight: 500, fontSize: "0.78rem" }}
        >
          {summaryLabel || countLabel}
          {summaryLabel && (
            <Typography
              component="span"
              variant="caption"
              sx={{ color: grey[400], fontWeight: 400, ml: 0.75 }}
            >
              · {callCount} tool{callCount === 1 ? "" : "s"}
            </Typography>
          )}
        </Typography>
      </ButtonBase>
      <Collapse in={expanded}>
        <Box
          sx={{
            ml: "10px",
            mt: 0.25,
            pl: 1.5,
            borderLeft: `2px solid ${grey[200]}`,
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

export default ToolRunGroup;
